import React, {useMemo, useState} from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Grid,
    Step,
    StepLabel,
    Stepper,
    Stack,
    TextField,
    Typography,
    MenuItem,
    Alert,
    Divider,
} from "@mui/material";
import {createDeferredAction, createScript, countEvalResults} from "../api/AiAgentApi";
import CreateScriptForm from "./CreateScriptForm";
import {useNavigate} from "react-router-dom";

/** map enum → подсказка по типу */
const TYPE_HINT = {
    STRING: "строка",
    INTEGER: "целое число",
    DOUBLE: "число",
    BOOLEAN: "true/false",
    DATETIME: "ISO-8601 datetime",
    UUID: "UUID",
};

/**
 * Wizard со Stepper:
 *  1) Создание скрипта (<CreateScriptForm/>)
 *  2) Параметры и запуск (тип + остальные поля + создание отложенного действия)
 */
export default function CreateDeferredActionWizard() {
    const navigate = useNavigate();

    // === Stepper logic ===
    const [activeStep, setActiveStep] = useState(0);
    const steps = ["Создать скрипт", "Создать отложенное действие"];

    const handleNext = () => setActiveStep((s) => Math.min(s + 1, steps.length - 1));
    const handleBack = () => setActiveStep((s) => Math.max(s - 1, 0));

    // === Данные из шага 1 (CreateScriptForm) ===
    const [script, setScript] = useState(null);
    const [loadingScript] = useState(false);

    // === Данные шага 2: тип + поля для создания отложенного действия ===
    const [deferredType, setDeferredType] = useState("One-time");
    const [actionName, setActionName] = useState("");
    const [intervalMinutes, setIntervalMinutes] = useState("60");
    const [externalValues, setExternalValues] = useState({});
    const [errors, setErrors] = useState({
        name: "",
        intervalMinutes: "",
        params: {}
    });

    // === Состояния для подсчета пользователей ===
    const [countLoading, setCountLoading] = useState(false);
    const [userCount, setUserCount] = useState(null);
    const [countError, setCountError] = useState(null);

    const externalParams = useMemo(
        () => (script?.params || []).filter((p) => String(p.source) === "EXTERNAL"),
        [script]
    );

    function validateFinal() {
        const e = { name: "", intervalMinutes: "", params: {} };

        if (!actionName || actionName.trim().length === 0) {
            e.name = "Укажите название отложенного действия";
        }

        if (deferredType === "Regular" && (!intervalMinutes || isNaN(Number(intervalMinutes)))) {
            e.intervalMinutes = "Укажите корректный интервал в минутах";
        }

        // Проверка обязательных внешних параметров
        for (const p of externalParams) {
            if (p.required) {
                const val = externalValues[p.id];
                if (val === undefined || val === null || String(val).trim() === "") {
                    e.params[p.id] = "Обязательное поле";
                }
            }
        }

        setErrors(e);
        const hasParamErrors = Object.keys(e.params).length > 0;
        return !e.name && !e.intervalMinutes && !hasParamErrors;
    }

    // Функция для подсчета пользователей
    const handleCountUsers = async () => {
        if (!script || !script.condition) {
            setCountError("Сначала заполните условия в скрипте");
            return;
        }

        setCountLoading(true);
        setCountError(null);
        setUserCount(null);

        try {
            // Формируем payload для подсчета
            const paramsPayload = externalParams.map((p) => ({
                value: String(externalValues[p.id] ?? p.defaultValue ?? ""),
                scriptParamId: p.id,
                name: p.name
            }));

            const payload = {
                condition: script.condition,
                deferredAction: {
                    name: actionName || "temp_name",
                    scriptId: 0, // Временный ID, т.к. скрипт еще не создан
                    intervalMinutes: deferredType === "Regular" ? Number(intervalMinutes) : 0,
                    params: paramsPayload
                },
                script: {
                    title: script.title,
                    templateBody: script.templateBody,
                    condition: script.condition,
                    params: script.params.map(p => ({
                        name: p.name,
                        source: p.source,
                        required: p.required,
                        defaultValue: p.defaultValue,
                        resolverPath: p.resolverPath
                    }))
                }
            };

            const count = await countEvalResults(payload);
            setUserCount(count);
        } catch (error) {
            console.error("Ошибка при подсчете пользователей:", error);
            setCountError(error.message || "Произошла ошибка при подсчете");
        } finally {
            setCountLoading(false);
        }
    };

    async function onSubmit() {
        if (!validateFinal()) return;

        // 1) создаём скрипт из того, что вернул CreateScriptForm
        const created = await createScript(script);

        // 2) собираем payload для отложенного действия
        const paramsPayload = externalParams.map((p) => ({
            scriptParamId: p.id,
            value: String(externalValues[p.id] ?? ""),
        }));

        const payload = {
            name: actionName.trim(),
            intervalMinutes: deferredType === "Regular" ? Number(intervalMinutes) : null,
            scriptId: created.id,
            params: paramsPayload,
        };

        await createDeferredAction(payload);
        navigate("/deferred-actions");
    }

    // Функция для отображения полей внешних параметров
    const renderExternalParamField = (param) => {
        const error = errors.params[param.id];

        if (param.type === "BOOLEAN") {
            return (
                <TextField
                    select
                    label={`${param.name}${param.required ? ' *' : ''}`}
                    value={externalValues[param.id] || ''}
                    onChange={(e) => setExternalValues({
                        ...externalValues,
                        [param.id]: e.target.value
                    })}
                    error={!!error}
                    helperText={error || TYPE_HINT[param.type]}
                    fullWidth
                    size="small"
                >
                    <MenuItem value="true">true</MenuItem>
                    <MenuItem value="false">false</MenuItem>
                </TextField>
            );
        }

        return (
            <TextField
                label={`${param.name}${param.required ? ' *' : ''}`}
                value={externalValues[param.id] || ''}
                onChange={(e) => setExternalValues({
                    ...externalValues,
                    [param.id]: e.target.value
                })}
                error={!!error}
                helperText={error || TYPE_HINT[param.type] || param.defaultValue ? `По умолчанию: ${param.defaultValue}` : ''}
                placeholder={param.defaultValue || ''}
                fullWidth
                size="small"
                type={param.type === "INTEGER" || param.type === "DOUBLE" ? "number" : "text"}
            />
        );
    };

    return (
        <Box>
            <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {/* ШАГ 1: Создание скрипта */}
            {activeStep === 0 && (
                <Box>
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Typography variant="subtitle1" sx={{ mb: 1 }}>
                                Шаг 1 — Создайте скрипт
                            </Typography>
                            <CreateScriptForm
                                requestScriptBody={script}
                                onNext={(scriptDraft) => {
                                    setScript(scriptDraft);
                                    handleNext();
                                }}
                            />
                        </CardContent>
                    </Card>
                </Box>
            )}

            {/* ШАГ 2: Параметры и запуск */}
            {activeStep === 1 && (
                <Box>
                    <Card sx={{ mb: 2 }}>
                        <CardContent>
                            <Stack spacing={3}>
                                <Typography variant="subtitle1">
                                    Шаг 2 — Настройте отложенное действие
                                </Typography>

                                <Grid container spacing={2}>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            select
                                            label="Тип отложенного действия"
                                            value={deferredType}
                                            onChange={(e) => setDeferredType(e.target.value)}
                                            fullWidth
                                            size="small"
                                            required
                                        >
                                            <MenuItem value="One-time">Разовая</MenuItem>
                                            <MenuItem value="Regular">Регулярная</MenuItem>
                                        </TextField>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            label="Название отложенного действия"
                                            value={actionName}
                                            onChange={(e) => setActionName(e.target.value)}
                                            error={!!errors.name}
                                            helperText={errors.name}
                                            fullWidth
                                            size="small"
                                            required
                                        />
                                    </Grid>

                                    {deferredType === "Regular" && (
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                label="Интервал (минуты)"
                                                value={intervalMinutes}
                                                onChange={(e) => setIntervalMinutes(e.target.value)}
                                                error={!!errors.intervalMinutes}
                                                helperText={errors.intervalMinutes}
                                                fullWidth
                                                size="small"
                                                type="number"
                                                required
                                            />
                                        </Grid>
                                    )}
                                </Grid>

                                {/* Внешние параметры */}
                                {externalParams.length > 0 && (
                                    <>
                                        <Divider />
                                        <Typography variant="subtitle2">
                                            Внешние параметры
                                        </Typography>
                                        <Grid container spacing={2}>
                                            {externalParams.map((param) => (
                                                <Grid item xs={12} md={6} key={param.id}>
                                                    {renderExternalParamField(param)}
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </>
                                )}

                                {/* Кнопка подсчета пользователей */}
                                <Box>
                                    <Button
                                        variant="outlined"
                                        onClick={handleCountUsers}
                                        disabled={countLoading || !script}
                                        startIcon={countLoading ? <CircularProgress size={20} /> : null}
                                        fullWidth
                                    >
                                        {countLoading ? "Подсчет..." : "Подсчитать количество пользователей"}
                                    </Button>

                                    {/* Результаты подсчета */}
                                    {userCount !== null && (
                                        <Alert
                                            severity="info"
                                            sx={{ mt: 2 }}
                                            action={
                                                <Button
                                                    color="inherit"
                                                    size="small"
                                                    onClick={handleCountUsers}
                                                >
                                                    Обновить
                                                </Button>
                                            }
                                        >
                                            <Typography variant="body2">
                                                Количество пользователей, которые попадут под условия: <strong>{userCount}</strong>
                                            </Typography>
                                        </Alert>
                                    )}

                                    {countError && (
                                        <Alert severity="error" sx={{ mt: 2 }}>
                                            {countError}
                                        </Alert>
                                    )}
                                </Box>

                                <Divider />

                                {/* Кнопки навигации */}
                                <Stack direction="row" spacing={2} justifyContent="space-between">
                                    <Button variant="outlined" onClick={handleBack}>
                                        Назад
                                    </Button>
                                    <Button
                                        variant="contained"
                                        onClick={onSubmit}
                                        disabled={!script}
                                    >
                                        Создать отложенное действие
                                    </Button>
                                </Stack>
                            </Stack>
                        </CardContent>
                    </Card>
                </Box>
            )}
        </Box>
    );
}